import { useEffect, useState } from "react";
import {
  getCacheKey,
  getFromMemory,
  acquireImage,
  releaseImage,
} from "utils/imageCache";

/**
 * Resolves a remote image URL to a cached object URL when possible.
 * Returns { src, isResolving }.
 *  - Non-cacheable sources (local assets, blob:, data:) pass through instantly.
 *  - On any cache/fetch failure it falls back to the original URL.
 */
const useCachedImage = (originalSrc) => {
  const key = getCacheKey(originalSrc);

  // Synchronous result for passthrough and same-session hits (no flicker).
  const immediate = !key ? originalSrc : getFromMemory(key);

  const [resolved, setResolved] = useState({ key: null, src: null });

  useEffect(() => {
    if (!key || immediate) return undefined;

    const controller = new AbortController();
    let acquired = false;

    acquireImage(key, originalSrc, controller.signal)
      .then((objectUrl) => {
        if (controller.signal.aborted) return;
        if (objectUrl) acquired = true;
        // null -> fall back to the network URL
        setResolved({ key, src: objectUrl || originalSrc });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setResolved({ key, src: originalSrc });
        }
      });

    return () => {
      controller.abort();
      if (acquired) releaseImage(key);
    };
  }, [key, originalSrc, immediate]);

  if (immediate) return { src: immediate, isResolving: false };
  if (resolved.key === key && resolved.src) {
    return { src: resolved.src, isResolving: false };
  }
  return { src: null, isResolving: Boolean(key) };
};

export default useCachedImage;
